import { getAnalytics, logEvent, isSupported } from 'firebase/analytics';
import { app } from '../config/firebase';

let analytics: ReturnType<typeof getAnalytics> | null = null;

async function getAnalyticsInstance() {
  if (analytics) return analytics;
  if (import.meta.env.MODE === 'development') return null; // No analytics in emulators
  const supported = await isSupported();
  if (!supported) return null;
  analytics = getAnalytics(app);
  return analytics;
}

export async function trackEvent(
  event: string,
  params?: Record<string, string | number | boolean>,
) {
  const instance = await getAnalyticsInstance();
  if (!instance) return;
  logEvent(instance, event, params);
}

// ── Error tracking ────────────────────────────────────────────────────────────

export function trackError(context: string, error: unknown) {
  const msg = error instanceof Error ? error.message : String(error);
  void trackEvent('app_error', { context, error_message: msg.slice(0, 100) });
}

// ── Pre-defined events ────────────────────────────────────────────────────────

export const Analytics = {
  // Auth
  loginSuccess: (method: string) => trackEvent('login', { method }),
  loginFailed: (method: string, reason?: string) =>
    trackEvent('login_failed', { method, ...(reason ? { reason: reason.slice(0, 50) } : {}) }),
  registro: () => trackEvent('sign_up'),
  logout: () => trackEvent('logout'),
  passwordReset: () => trackEvent('password_reset_requested'),

  // Queue & sala
  medicoEntrarSala: (salaId: string) => trackEvent('medico_entrar_sala', { sala_id: salaId }),
  medicoSalirSala: (salaId: string) => trackEvent('medico_salir_sala', { sala_id: salaId }),
  pacienteEnCola: (salaId: string, posicion: number) =>
    trackEvent('paciente_en_cola', { sala_id: salaId, posicion }),
  asignacionAceptada: (salaId: string) => trackEvent('asignacion_aceptada', { sala_id: salaId }),
  asignacionRechazada: (salaId: string) => trackEvent('asignacion_rechazada', { sala_id: salaId }),
  pacienteTransferido: (salaOrigenId: string, salaDestinoId: string) =>
    trackEvent('paciente_transferido', { sala_origen: salaOrigenId, sala_destino: salaDestinoId }),

  // Video call
  consultaIniciada: (salaId: string) => trackEvent('consulta_iniciada', { sala_id: salaId }),
  consultaFinalizada: (disposicion: string, duracion: number) =>
    trackEvent('consulta_finalizada', { disposicion, duracion_segundos: duracion }),
  livekitConectado: (consultaId: string) => trackEvent('livekit_connected', { consulta_id: consultaId }),
  livekitDesconectado: (consultaId: string, reason?: string) =>
    trackEvent('livekit_disconnected', { consulta_id: consultaId, ...(reason ? { reason } : {}) }),
  livekitError: (consultaId: string, error: string) =>
    trackEvent('livekit_error', { consulta_id: consultaId, error: error.slice(0, 100) }),

  // AI / notas
  notaIAGenerada: () => trackEvent('nota_ia_generada'),
  transcripcionIniciada: () => trackEvent('transcripcion_iniciada'),
  transcripcionDetenida: (duracion: number) =>
    trackEvent('transcripcion_detenida', { duracion_segundos: duracion }),

  // Patient
  pacienteBuscado: (tipo: 'cedula' | 'nombre') => trackEvent('paciente_buscado', { tipo }),
  pacienteCreado: () => trackEvent('paciente_creado'),
  datosExportados: () => trackEvent('datos_exportados'),

  // Navigation
  pageView: (page: string) => trackEvent('page_view', { page_path: page }),
};
